import { ThemeSpaceVariable } from "@caspeco/casper-ui-library.base-ui.theme";
import { Box } from "@caspeco/casper-ui-library.components.box";
import { Text } from "@caspeco/casper-ui-library.components.text";
import { SafeHTML } from "components/safe-html";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface IShowMoreText {
    text: string;
    initialLines?: number;
    showMoreText?: string;
    showLessText?: string;
    textProps?: React.ComponentProps<typeof Text>;
    linkProps?: React.ComponentProps<typeof Text>;
}

const ShowMoreText = ({
    text,
    initialLines = 3,
    showMoreText,
    showLessText,
    textProps = {},
    linkProps = {},
}: IShowMoreText) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const showMoreRef = useRef<HTMLDivElement>(null);
    const [gradiantWidth, setGradiantWidth] = useState(0);
    const { t } = useTranslation();
    // Create a unique ID for the text element to avoid conflicts with other elements on the page
    const textId = `show-more-text-${Math.random().toString(36).substring(2, 9)}`;

    useEffect(() => {
        const element = document.getElementById(textId);
        if (!element) return;

        const checkTruncation = () => {
            const element = document.getElementById(textId);
            if (!element) return;

            if (isExpanded) return;

            const originalLineClamp = element.style.webkitLineClamp;
            const truncatedHeight = element.clientHeight;

            element.style.webkitLineClamp = "none";
            const fullHeight = element.clientHeight;
            element.style.webkitLineClamp = originalLineClamp;

            const wasTruncated = fullHeight > truncatedHeight;
            setIsTruncated(wasTruncated);
        };

        // Initial check after render
        const timer = setTimeout(checkTruncation, 0);

        setGradiantWidth(
            showMoreRef.current?.clientWidth ? showMoreRef.current.clientWidth * 2.1 : 0
        );
        // Check on resize
        const resizeObserver = new ResizeObserver(checkTruncation);
        resizeObserver.observe(element);

        return () => {
            clearTimeout(timer);
            resizeObserver.disconnect();
        };
    }, [text, initialLines, textId, isExpanded]);

    if (!text) return null;

    const defaultShowMoreText = showMoreText || t("showMoreText.showMore");
    const defaultShowLessText = showLessText || t("showMoreText.showLess");

    const showButton = isTruncated;

    const ShowMoreButton = () => (
        <div onClick={() => setIsExpanded(!isExpanded)} aria-hidden="true" ref={showMoreRef}>
            <Text fontWeight="400" textDecoration="underline" cursor="pointer" {...linkProps}>
                {isExpanded ? defaultShowLessText : defaultShowMoreText}
            </Text>
        </div>
    );

    return (
        <Box position="relative" display="flex" flexDirection="column">
            <Text id={textId} noOfLines={isExpanded ? undefined : initialLines} {...textProps}>
                <SafeHTML
                    element="span"
                    options={{
                        allowedTags: ["a", "br"],
                        allowedSchemes: ["tel", "mailto", "https"],
                    }}
                    html={text}
                />
            </Text>

            {showButton && !isExpanded && (
                <Box
                    position="absolute"
                    bottom="0"
                    right="0"
                    height={`${showMoreRef.current?.clientHeight}px`}
                    width={`${gradiantWidth}px`}
                    background="linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 40%, rgba(255,255,255,1) 100%)"
                    zIndex="1"
                    pointerEvents="none"
                />
            )}

            {showButton && !isExpanded && (
                <Box
                    position="absolute"
                    bottom="0"
                    right="0"
                    height={`${showMoreRef.current?.clientHeight}px`}
                    paddingLeft={ThemeSpaceVariable.Small}
                    paddingRight={ThemeSpaceVariable.Small}
                    display="flex"
                    alignItems="center"
                    zIndex="2"
                >
                    <ShowMoreButton />
                </Box>
            )}
            {isExpanded && <ShowMoreButton />}
        </Box>
    );
};

export default ShowMoreText;
